import React, { useState } from "react";
import { format } from "date-fns";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Download, FileDown, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useCustomer } from "@/context/CustomerContext";
import PanIndiaFilter from "@/components/customer/PanIndiaFilter";
import ExpiredScreen from "@/components/customer/ExpiredScreen";

function DateField({ value, onChange, testid, placeholder }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="w-full h-10 flex items-center justify-between rounded-md border border-border bg-background px-3 text-sm hover:border-primary/50 transition-colors">
          <span className={value ? "num" : "text-muted-foreground"}>{value ? format(value, "dd MMM yyyy") : placeholder}</span>
          <span className="flex items-center gap-1">
            {value && <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); onChange(undefined); }} />}
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-popover" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

export default function DataExport() {
  const { loading, active, subscription, isPanIndia, filterOpts } = useCustomer();
  const [dtype, setDtype] = useState("both");
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!active) return <ExpiredScreen />;

  const subDtype = subscription?.data_type || "both";
  const dtypeOptions = subDtype === "both" ? [
    { value: "both", label: "GST + MCA" },
    { value: "gst", label: "GST only" },
    { value: "mca", label: "MCA only" },
  ] : [{ value: subDtype, label: subDtype.toUpperCase() + " only" }];

  const buildPayload = () => ({
    dtype: subDtype === "both" ? dtype : subDtype,
    date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : null,
    date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : null,
    state: isPanIndia ? (fState || null) : null,
    city: isPanIndia ? (fCity || null) : null,
  });

  const doCount = async () => {
    setBusy(true); setCount(null);
    try {
      const { data } = await api.post("/customer/data-export/count", buildPayload());
      setCount(data.count);
      toast.success(`${data.count.toLocaleString()} record(s) match`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/customer/data-export/download`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!r.ok) {
        let msg = `Download failed (${r.status})`;
        try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (err) { void err; }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "export.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${fname}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex items-center gap-2">
        <div className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center">
          <FileDown className="h-4 w-4" />
        </div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl">Data Export</h1>
      </div>
      <p className="text-sm text-muted-foreground -mt-4 mb-6">Filter data by date range and download a single CSV. Filters stay within your subscription window.</p>

      <PanIndiaFilter isPanIndia={isPanIndia} filterOpts={filterOpts} fState={fState} setFState={setFState} fCity={fCity} setFCity={setFCity} />

      <Card className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label>Data type</Label>
            <Select value={subDtype === "both" ? dtype : subDtype} onValueChange={setDtype} disabled={subDtype !== "both"}>
              <SelectTrigger data-testid="export-dtype"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">
                {dtypeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From date</Label>
            <DateField value={dateFrom} onChange={setDateFrom} testid="export-date-from" placeholder="Subscription start" />
          </div>
          <div>
            <Label>To date</Label>
            <DateField value={dateTo} onChange={setDateTo} testid="export-date-to" placeholder="Subscription end" />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={doCount} disabled={busy} data-testid="export-count-btn">
            <Search className="h-4 w-4 mr-1.5" /> Count records
          </Button>
          <Button onClick={doDownload} disabled={busy} data-testid="export-download-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download CSV
          </Button>
          {count !== null && (
            <div className="text-sm num text-muted-foreground" data-testid="export-count-result">
              {count.toLocaleString()} record(s) match
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
